import { Suspense, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { BottomSheet, Button, Checkbox, Text } from '@thefarmersfront/kitchen-mobile';
import clsx from 'clsx';
import { TypeCreateWorkApplicationsRequest } from '~/api/workApplications';

import Spinner from '~/components/LoadingIndicator/Spinner';

import { useGetRecommendations } from '~/hooks/api/useGetRecommendations';
import { type AdditionalSubmitResult, useSubmitAdditionalWorkApply } from '~/hooks/api/useSubmitAdditionalWorkApply';
import { BottomSheetController } from '~/services/bottomSheetService';
import { formatDate } from '~/utils';

import { RecruitBadge } from '../../RecruitBadge';

import styles from './AdditionalSuggestionBottomSheet.module.scss';

interface AdditionalSuggestionBottomSheetProps extends BottomSheetController<AdditionalSubmitResult> {
  params?: TypeCreateWorkApplicationsRequest;
  workApplicationId: string;
}

const AdditionalSuggestionContent = ({
  workApplicationId,
  params,
  onClose,
}: Pick<AdditionalSuggestionBottomSheetProps, 'workApplicationId' | 'params' | 'onClose'>) => {
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const { mutateAsync: submitAdditionalDates, isPending } = useSubmitAdditionalWorkApply();
  const { data } = useGetRecommendations({ workApplicationId });
  const { recommendations } = data;

  const dates = useMemo(
    () =>
      recommendations.map(({ workDate }) => ({
        date: workDate,
        label: formatDate({ value: workDate, format: 'M월 D일(dd)' }),
      })),
    [recommendations]
  );

  const preferences = useMemo(() => {
    const firstRecommendation = recommendations[0];

    if (!firstRecommendation) return [];

    const firstApp = firstRecommendation.applications.find((a) => a.priority.code === 'FIRST');
    const secondApp = firstRecommendation.applications.find((a) => a.priority.code === 'SECOND');

    return [
      ...(firstApp
        ? [
            {
              label: '1지망' as const,
              workShift: firstApp.workShift,
              workEnvironment: firstApp.workEnvironment.text,
            },
          ]
        : []),
      ...(secondApp
        ? [
            {
              label: '2지망' as const,
              workShift: secondApp.workShift,
              workEnvironment: secondApp.workEnvironment.text,
            },
          ]
        : []),
    ];
  }, [recommendations]);

  const handleToggle = (date: string) => {
    setSelectedDates((prev) => (prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]));
  };

  const handleSkip = () => {
    onClose({ type: 'skipped' });
  };

  const handleSubmit = async () => {
    if (!params) return;

    const selections = selectedDates.flatMap((date) => {
      const recommendation = recommendations.find((r) => r.workDate === date);
      const firstApp = recommendation?.applications.find((a) => a.priority.code === 'FIRST');
      if (!recommendation || !firstApp) return [];

      const secondApp = recommendation.applications.find((a) => a.priority.code === 'SECOND');
      return [
        {
          workDate: date,
          firstRecruitNoticeId: firstApp.recruitNoticeId,
          secondRecruitNoticeId: secondApp?.recruitNoticeId ?? null,
        },
      ];
    });

    if (selections.length === 0) return;

    const result = await submitAdditionalDates({ params, selections });
    onClose(result);
  };

  return (
    <>
      {preferences.length > 0 && (
        <div className={styles.preferenceGroup}>
          {preferences.map((preference) => (
            <div key={preference.label} className={styles.preferenceRow}>
              <RecruitBadge value={preference.label} />
              <div className={styles.preferenceInfo}>
                <Text size="large" color="secondary">
                  {preference.workShift}
                </Text>
                <span className={styles.divider} />
                <Text size="large" color="secondary">
                  {preference.workEnvironment}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={styles.body}>
        {dates.map(({ date, label }) => {
          const isSelected = selectedDates.includes(date);
          return (
            <button
              key={date}
              type="button"
              className={clsx(styles.listItem, isSelected && styles.selected)}
              onClick={() => handleToggle(date)}
              disabled={isPending}
            >
              <Checkbox color="primary" checked={isSelected} readOnly touchArea="2px" />
              <span className={styles.dateLabel}>{label}</span>
            </button>
          );
        })}
      </div>
      <div className={styles.buttonGroup}>
        <Button color="tertiary" onClick={handleSkip} disabled={isPending}>
          건너뛰기
        </Button>
        <Button onClick={handleSubmit} disabled={selectedDates.length === 0 || isPending}>
          {selectedDates.length > 0 ? `${selectedDates.length}건 근무 지원` : '근무 지원'}
        </Button>
      </div>
    </>
  );
};

const AdditionalSuggestionBottomSheet = ({
  open,
  onClose,
  params,
  workApplicationId,
}: AdditionalSuggestionBottomSheetProps) => {
  return (
    <BottomSheet
      id="additional-suggestion"
      className={styles.additionalSuggestionBottomSheet}
      open={open}
      title="다른 근무일도 지원해보세요"
      subTitle="같은 조건으로 근무할 수 있어요."
      showHandbar={false}
      onClose={onClose}
      showButton={false}
    >
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallbackRender={({ resetErrorBoundary }) => (
              <div className={styles.error}>
                <Text size="large" color="secondary">
                  정보를 불러오지 못했어요.
                </Text>
                <button type="button" className={styles.retryButton} onClick={resetErrorBoundary}>
                  <Text size="medium" color="primary">
                    다시 시도
                  </Text>
                </button>
              </div>
            )}
          >
            <Suspense
              fallback={
                <div className={styles.fallback}>
                  <Spinner />
                </div>
              }
            >
              <AdditionalSuggestionContent workApplicationId={workApplicationId} onClose={onClose} params={params} />
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </BottomSheet>
  );
};

export default AdditionalSuggestionBottomSheet;
